// ReviewRocket engine — Google review SMS/email campaigns + tracking.

export interface ReviewRequest {
  id: string;
  customerName: string;
  customerContact: string;  // phone OR email
  channel: 'sms' | 'email';
  serviceName: string;
  sentISO: string;
  status: 'queued' | 'sent' | 'opened' | 'clicked' | 'reviewed' | 'declined';
  reviewedISO?: string;
  template: string;          // template id used
  trackingId: string;
}

export interface BusinessProfile {
  name: string;
  googleReviewUrl: string;  // https://search.google.com/local/writereview?placeid=...
  brand: string;            // short brand for SMS
  signature: string;        // signoff
}

export const TEMPLATES = {
  sms_friendly: { id: 'sms_friendly', channel: 'sms', name: 'Friendly (SMS)',
    body: 'Hi {NAME}! Thanks for choosing {BRAND}. If we did right by you today, a quick Google review would mean the world: {LINK}\n- {SIGNATURE}' },
  sms_short: { id: 'sms_short', channel: 'sms', name: 'Ultra-short (SMS)',
    body: '{BRAND}: Mind dropping us a quick Google review? {LINK} 🙏' },
  sms_thank: { id: 'sms_thank', channel: 'sms', name: 'Thank-you (SMS)',
    body: 'Hey {NAME}, we really appreciate your business. A 2-min Google review helps us a lot: {LINK}' },
  email_warm: { id: 'email_warm', channel: 'email', name: 'Warm follow-up (Email)',
    body: `Subject: A quick favor, {NAME}?\n\nHi {NAME},\n\nThanks for trusting us with {SERVICE}. Reviews from neighbours like you are how new customers find us — would you mind leaving a quick Google review?\n\n👉 {LINK}\n\nIt takes 60 seconds. Thank you!\n\n— {SIGNATURE}` },
  email_referral: { id: 'email_referral', channel: 'email', name: 'Referral angle (Email)',
    body: `Subject: Did we earn 5 stars from you?\n\n{NAME}, if we earned 5 stars today, would you share a quick Google review? It's the single best way friends and neighbours find us.\n\n👉 {LINK}\n\nGratefully,\n{SIGNATURE}` },
} as const;

export function renderTemplate(tpl: typeof TEMPLATES[keyof typeof TEMPLATES], vars: { NAME: string; BRAND: string; LINK: string; SIGNATURE: string; SERVICE: string }): string {
  return tpl.body.replace(/\{NAME\}/g, vars.NAME).replace(/\{BRAND\}/g, vars.BRAND).replace(/\{LINK\}/g, vars.LINK).replace(/\{SIGNATURE\}/g, vars.SIGNATURE).replace(/\{SERVICE\}/g, vars.SERVICE);
}

export function trackingUrl(baseReviewUrl: string, trackingId: string): string {
  const u = new URL(baseReviewUrl);
  u.searchParams.set('utm_source', 'review_rocket');
  u.searchParams.set('utm_campaign', trackingId);
  return u.toString();
}

export function metrics(requests: ReviewRequest[]): { sent: number; opened: number; clicked: number; reviewed: number; declineRate: number; reviewRate: number } {
  const sent = requests.filter(r => ['sent','opened','clicked','reviewed'].includes(r.status)).length;
  const opened = requests.filter(r => ['opened','clicked','reviewed'].includes(r.status)).length;
  const clicked = requests.filter(r => ['clicked','reviewed'].includes(r.status)).length;
  const reviewed = requests.filter(r => r.status === 'reviewed').length;
  const declined = requests.filter(r => r.status === 'declined').length;
  return {
    sent, opened, clicked, reviewed,
    declineRate: sent > 0 ? (declined / sent) * 100 : 0,
    reviewRate: sent > 0 ? (reviewed / sent) * 100 : 0,
  };
}

export function defaultProfile(): BusinessProfile {
  return { name: '', googleReviewUrl: '', brand: '', signature: '' };
}

export function exportRequestsCSV(reqs: ReviewRequest[]): string {
  const headers = ['Name','Contact','Channel','Service','Sent','Status','Reviewed','Tracking'];
  const rows = reqs.map(r => [r.customerName, r.customerContact, r.channel, r.serviceName, r.sentISO, r.status, r.reviewedISO || '', r.trackingId]);
  return [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
}
